import { useAuth } from "../lib/auth";
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock3, Copy, Mail, Plus, Send, Trash2 } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { GlChoiceGrid, glName } from "../components/GlChoiceGrid";
import CompanyUsage from "../components/CompanyUsage";
import BillingPanel from "../components/BillingPanel";

// Prompt 15 PART B — Company settings page.
// Everything company-level lives under /settings. Today that's the
// billing-emails list (add → outbox confirmation link → confirm/remove)
// and (for super admins) a "Send test email" button that exercises the
// Resend transport. This is also the home for future tenant-level
// settings (receiving GL default, notification preferences, team).
export default function Settings() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [company, setCompany] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [savingGl, setSavingGl] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ["usage", "billing"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "general";
  const setTab = (t) => setSearchParams(t === "general" ? {} : { tab: t }, { replace: true });

  const load = useCallback(async () => {
    // Each call is independent — a super-admin login has no company
    // and will 400 on /billing-emails, but we still want to render
    // the admin-only "Send test email" button from /companies/me.
    const [a, b, c] = await Promise.all([
      api.get("/companies/me/billing-emails").catch((e) => ({ data: null, _err: e })),
      api.get("/email-outbox").catch((e) => ({ data: null, _err: e })),
      api.get("/companies/me").catch((e) => ({ data: null, _err: e })),
    ]);
    setRows(a.data?.billing_emails || []);
    setOutbox(b.data?.outbox || []);
    setCompany(c.data?.company || null);
    setIsSuperAdmin(Boolean(c.data?.is_super_admin));
    // Only surface an error if ALL three failed — a 400 on billing-emails
    // for a super-admin without a company is expected and not noise.
    if (!a.data && !b.data && !c.data) {
      setNotice({ kind: "error",
                  text: a._err?.response?.data?.detail || "Failed to load settings" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build a lookup of pending outbox messages so we can show the
  // confirmation link next to the unconfirmed row.
  const outboxByEmail = outbox.reduce((acc, m) => {
    if (m.kind === "billing_email_confirmation") {
      acc[m.to] = acc[m.to] || m;
    }
    return acc;
  }, {});

  const add = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    setNotice(null);
    try {
      await api.post("/companies/me/billing-emails", { email });
      setNotice({ kind: "success",
                  text: `Confirmation sent to ${email}. The outbox below also lists what we tried to deliver.` });
      setNewEmail("");
      await load();
    } catch (e) {
      setNotice({ kind: "error",
                  text: e?.response?.data?.detail || "Could not add email" });
    } finally {
      setAdding(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setNotice(null);
    try {
      const r = await api.post("/admin/send-test-email");
      if (r.data?.ok) {
        setNotice({ kind: "success",
          text: `Test email sent to ${r.data.to} (message id: ${r.data.message_id}).` });
      } else {
        setNotice({ kind: "error",
          text: `Test email failed: ${r.data?.error || "unknown error"}` });
      }
      await load();
    } catch (e) {
      setNotice({ kind: "error",
                  text: e?.response?.data?.detail || "Test email request failed" });
    } finally {
      setSendingTest(false);
    }
  };

  const removeOne = async (email) => {
    if (!confirm(`Remove ${email} from billing emails?`)) return;
    try {
      await api.delete(`/companies/me/billing-emails/${encodeURIComponent(email)}`);
      setNotice({ kind: "success", text: `Removed ${email}.` });
      await load();
    } catch (e) {
      setNotice({ kind: "error",
                  text: e?.response?.data?.detail || "Could not remove email" });
    }
  };

  // Non-production shortcut: confirm the email directly from the UI by
  // following the outbox link. Once Resend is wired this disappears for
  // the end user (the real recipient clicks the link in their inbox).
  const confirmFromOutbox = async (email, url) => {
    setConfirming(email);
    try {
      // Hitting the public confirm endpoint directly.
      const token = (url || "").split("token=")[1];
      if (!token) throw new Error("No token in outbox URL");
      await api.get(`/billing-emails/confirm?token=${token}`);
      setNotice({ kind: "success", text: `Confirmed ${email}.` });
      await load();
    } catch (e) {
      setNotice({ kind: "error",
                  text: e?.response?.data?.detail || "Could not confirm" });
    } finally {
      setConfirming(null);
    }
  };

  const choosePrimaryGl = async (erp) => {
    setSavingGl(true);
    try {
      await api.put("/companies/me/primary-gl", { primary_gl: erp });
      setNotice({ kind: "success", text: `${glName(erp)} is now your accounting system and Receiving GL.` });
      await load();
    } catch (e) {
      setNotice({ kind: "error", text: e?.response?.data?.detail || "Could not save accounting system" });
    } finally {
      setSavingGl(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice({ kind: "success", text: "Link copied." });
    } catch (_e) { /* clipboard denied */ }
  };

  return (
    <Layout>
      <PageHeader
        title="Settings"
        description={company?.name
          ? `Company-level settings for ${company.name}.`
          : "Company-level settings."} />

      <div className="p-8 space-y-8" data-testid="settings-page">
        {notice && (
          <div
            className={`card-flat p-3 text-sm ${
              notice.kind === "success"
                ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                : "border-red-600 bg-red-50 text-red-700"}`}
            data-testid="settings-notice"
          >
            {notice.text}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="space-y-8">
          <TabsList data-testid="settings-tabs">
            <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
            {company ? <TabsTrigger value="usage" data-testid="settings-tab-usage">Usage</TabsTrigger> : null}
            {company ? <TabsTrigger value="billing" data-testid="settings-tab-billing">Billing</TabsTrigger> : null}
          </TabsList>
          <TabsContent value="usage">{company ? <CompanyUsage /> : null}</TabsContent>
          <TabsContent value="billing">{company && tab === "billing" ? <BillingPanel /> : null}</TabsContent>
          <TabsContent value="general" className="space-y-8">
        {company && !isSuperAdmin ? (
          <section className="card-flat p-6" data-testid="primary-gl-section">
            <div className="label-mini">accounting system</div>
            <h2 className="font-display text-xl font-semibold mt-1">Which accounting system do you use?</h2>
            <p className="text-sm text-neutral-600 mt-1 mb-5">
              Bills from your vendors go into this system. It is also your Receiving GL.
            </p>
            <GlChoiceGrid selected={company.primary_gl} onSelect={choosePrimaryGl} disabled={savingGl} />
          </section>
        ) : null}

        <section className="card-flat p-6" data-testid="security-section">
          <div className="label-mini">Security</div>
          <p className="text-sm mt-2" data-testid="last-signed-in">
            {user?.last_login_at
              ? `Last signed in ${new Date(user.last_login_at).toLocaleString()}`
              : "Last signed in: this is your first sign-in"}
          </p>
        </section>

        <section className="card-flat p-6" data-testid="billing-emails-section">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="label-mini">billing emails</div>
              <h2 className="font-display text-xl font-semibold mt-1">
                Where invoices for this company arrive
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                A vendor's GL ships an invoice to one of these addresses →
                Lingua Franca turns it into a bill on your Receiving GL.
                Each address can belong to one company only.
              </p>
            </div>
            <div className="shrink-0 flex items-end gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="ap@yourco.com"
                className="border border-neutral-900 px-3 py-1.5 font-mono text-sm w-64"
                data-testid="billing-email-input"
                onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              />
              <button
                type="button"
                onClick={add}
                disabled={adding || !newEmail.trim()}
                className="btn-primary flex items-center gap-2"
                data-testid="billing-email-add-btn"
              >
                <Plus size={12} /> {adding ? "sending…" : "add"}
              </button>
            </div>
          </div>

          <div className="border border-neutral-200">
            <div className="grid grid-cols-12 px-4 py-2 border-b border-neutral-200 label-mini">
              <div className="col-span-5">address</div>
              <div className="col-span-2">status</div>
              <div className="col-span-2">source</div>
              <div className="col-span-3 text-right">action</div>
            </div>
            {rows.length === 0 ? (
              <div className="px-4 py-16 text-center text-sm text-neutral-500"
                   data-testid="billing-emails-empty">
                You will see your signup email here as soon as it's loaded.
              </div>
            ) : rows.map((r) => {
              const pending = !r.confirmed ? outboxByEmail[r.email] : null;
              return (
                <div key={r.email}
                     className="grid grid-cols-12 items-center px-4 py-3 border-b border-neutral-100 text-sm"
                     data-testid={`billing-email-row-${r.email}`}>
                  <div className="col-span-5 flex items-center gap-2">
                    <Mail size={14} className="text-neutral-500" />
                    <span className="font-mono">{r.email}</span>
                  </div>
                  <div className="col-span-2">
                    {r.confirmed ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"
                            data-testid={`billing-email-status-${r.email}`}>
                        <CheckCircle2 size={12} /> confirmed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700"
                            data-testid={`billing-email-status-${r.email}`}>
                        <Clock3 size={12} /> pending
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-neutral-500">
                    {r.source || "—"}
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-2">
                    {pending ? (
                      <>
                        <button
                          type="button"
                          onClick={() => copyLink(pending.extra?.confirm_url)}
                          className="btn-outline flex items-center gap-1 text-xs"
                          data-testid={`billing-email-copy-${r.email}`}
                          title="Copy confirmation link"
                        >
                          <Copy size={10} /> copy link
                        </button>
                        <button
                          type="button"
                          onClick={() => confirmFromOutbox(r.email, pending.extra?.confirm_url)}
                          disabled={confirming === r.email}
                          className="btn-primary text-xs"
                          data-testid={`billing-email-confirm-${r.email}`}
                        >
                          {confirming === r.email ? "confirming…" : "open link"}
                        </button>
                      </>
                    ) : null}
                    {r.source !== "signup" ? (
                      <button
                        type="button"
                        onClick={() => removeOne(r.email)}
                        className="btn-outline flex items-center gap-1 text-xs"
                        data-testid={`billing-email-remove-${r.email}`}
                        title="Remove email"
                      >
                        <Trash2 size={10} /> remove
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="label-mini mt-3">
            confirmation emails are delivered via resend; the outbox below mirrors every attempt
          </div>
        </section>

        {isSuperAdmin ? (
          <section className="card-flat p-6" data-testid="admin-test-email-section">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="label-mini">admin · email transport</div>
                <h2 className="font-display text-xl font-semibold mt-1">
                  Send a test email
                </h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Sends a one-off test message to your logged-in address via
                  Resend. Use this to confirm the transport and your sender
                  domain are healthy.
                </p>
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={sendTestEmail}
                  disabled={sendingTest}
                  className="btn-primary flex items-center gap-2"
                  data-testid="admin-send-test-email-btn"
                >
                  <Send size={12} />
                  {sendingTest ? "sending…" : "send test email"}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        <section className="card-flat p-6" data-testid="email-outbox-section">
          <div className="label-mini">email outbox</div>
          <h2 className="font-display text-xl font-semibold mt-1">
            Last 25 outbound messages
          </h2>
          <p className="text-sm text-neutral-600 mt-1">
            Every delivery attempt made on your behalf — including the Resend message id when the send succeeded.
          </p>
          <div className="mt-4 border border-neutral-200">
            {outbox.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-neutral-500"
                   data-testid="email-outbox-empty">
                No outbound mail yet.
              </div>
            ) : outbox.map((m) => (
              <div key={m.id}
                   className="px-4 py-3 border-b border-neutral-100 text-sm"
                   data-testid={`email-outbox-row-${m.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-mono text-xs text-neutral-500">
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`label-mini ${
                        m.status === "sent"
                          ? "text-emerald-700"
                          : m.status === "failed"
                            ? "text-red-700"
                            : "text-neutral-500"}`}
                      data-testid={`email-outbox-status-${m.id}`}>
                      {m.status || "queued"}
                    </span>
                    <span className="label-mini">{m.kind}</span>
                  </div>
                </div>
                <div className="mt-1 font-medium">{m.subject}</div>
                <div className="text-xs text-neutral-500 mt-0.5">to {m.to}</div>
                {m.provider_message_id ? (
                  <div className="text-xs text-neutral-500 mt-0.5 font-mono">
                    resend id: {m.provider_message_id}
                  </div>
                ) : null}
                {m.provider_error ? (
                  <div className="text-xs text-red-700 mt-0.5">
                    error: {m.provider_error}
                  </div>
                ) : null}
                {m.extra?.confirm_url ? (
                  <div className="mt-2 text-xs font-mono break-all text-neutral-700">
                    {m.extra.confirm_url}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
